import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spin, Result, Button, Typography } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  LoadingOutlined,
  HomeOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { checkAuth, fetchSubInfo} from '../../../store/slices/auth.slice';
import subService from '../../../services/sub/sub.service';
import publicService from '../../../services/public/public.service';
import { all_routes } from '../../../router/all_routes';
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import authService from '../../../services/auth/auth.service';

const { Title, Text } = Typography;

interface PaymentVerificationResult {
  message: string;
  subscription: {
    status: string;
    type: string;
    referenceCode: string;
    expiryDate: string;
  };
}

const PaymentCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<PaymentVerificationResult | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        
        // localStorage'dan checkout session token'ını al
        const token = localStorage.getItem('checkoutsession');
        const conversationId = localStorage.getItem('conversationId');
        const companyId = localStorage.getItem("companyId");
        
        if (!token) {
          setResult({
            message: 'Ödeme doğrulama bulunamadı. Doğrulama zaten yapılmış olabilir.',
            subscription: {
              status: 'error',
              type: 'monthly',
              referenceCode: '',
              expiryDate: ''
            }
          });
          setLoading(false);
          return;
        }

        // 🔒 KRITIK GÜVENLİK: Token'ı hemen sil ki ikinci kez çalışırsa token bulamasın
        localStorage.removeItem('checkoutsession');
        localStorage.removeItem('conversationId');
        
        
        // Backend'e doğrulama isteği gönder - IYZICO conversation ID ile
        const verificationResult = await subService.verifyPayment(token, conversationId!, companyId!);


        // Response'u state'e kaydet
        setResult({
          message: verificationResult.message || 'Ödeme işlemi tamamlandı',
          subscription: verificationResult.subscription
        });

        // Eğer abonelik aktif ise
        if (verificationResult.subscription.status === 'active') {
          
          // Yeni kayıt için geçici admin bilgilerini kontrol et
          const tempNewAdminReg = localStorage.getItem('tempNewAdminRegistration');
          
          if (tempNewAdminReg) {
            
            try {
              const regData = JSON.parse(tempNewAdminReg);
              
              // Önce admini oluştur
              await publicService.addFirstAdmin(
                regData.companyId,
                regData.adminData.firstName,
                regData.adminData.lastName,
                regData.adminData.password,
                regData.adminData.email,
                regData.onboardingSecret
              );
              
              
              // Sonra login yap
              const loginResponse = await authService.login({
                email: regData.adminData.email,
                password: regData.adminData.password,
                isMobile: false
              });


              authService.setToken(loginResponse.tokens.accessToken);
              authService.setUser(loginResponse.user);
              dispatch(checkAuth());
              
              // Sub bilgilerini yenile
              if (loginResponse.user.companyId) {
                dispatch(fetchSubInfo(loginResponse.user.companyId));
              }
              
              // Geçici bilgileri temizle
              localStorage.removeItem('tempNewAdminRegistration');
              
              
            } catch (setupError: any) {
              handleApiError(setupError, t);
              // Ödeme başarılı ama hesap kurulumu başarısız oldu
              setResult(prev => ({
                ...prev!,
                success: false, 
                message: "Ödemeniz başarılı ancak hesap kurulumu sırasında bir hata oluştu. Lütfen destek ile iletişime geçin.",
                error: 'POST_PAYMENT_SETUP_FAILED'
              }));
            }
          } else {
            
            // Mevcut kullanıcı için sub bilgilerini yenile
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (user.companyId) {
              dispatch(fetchSubInfo(user.companyId));
            }
          }
        } else {
        }

      } catch (error: any) {
        
        setResult({
          message: error.response?.data?.message || 'Ödeme doğrulama sırasında bir hata oluştu.',
          subscription: {
            status: 'error',
            type: 'monthly',
            referenceCode: '',
            expiryDate: ''
          }
        });
        
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, []); // 🔒 GÜVENLIK: Dependency array'i boş - sadece mount'ta çalışsın

  const handleGoToDashboard = () => {
    // Eğer yeni kayıt varsa ve henüz login olmamışsa, login sayfasına yönlendir
    const tempNewAdminReg = localStorage.getItem('tempNewAdminRegistration');
    const currentUser = localStorage.getItem('user');
    
    if (tempNewAdminReg && !currentUser) {
      navigate(all_routes.login);
    } else {
      navigate(all_routes.adminDashboard);
    }
  };

  const handleGoToSettings = () => {
    navigate(all_routes.companySettings + '?tab=payment-settings');
  };

  const handleRetryPayment = () => {
    navigate(all_routes.companySettings + '?tab=payment-settings');
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Card style={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
              <div style={{ padding: '40px 20px' }}>
                <Spin 
                  indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
                  size="large"
                />
                <div style={{ marginTop: 24 }}>
                  <Title level={4}>Ödeme Doğrulanıyor...</Title>
                  <Text type="secondary">
                    Ödemeniz doğrulanıyor, lütfen bekleyin...
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Card style={{ maxWidth: 600, width: '100%' }}>
            {result?.subscription.status === 'active' ? (
              <Result
                icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 72 }} />}
                title={
                  <Title level={3} style={{ color: '#52c41a', marginBottom: 16 }}>
                    🎉 Aboneliğiniz Aktif!
                  </Title>
                }
                subTitle={
                  <div>
                    <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                      {result.message}
                    </Text>
                    {/* <Text style={{ fontSize: 16, display: 'block', marginBottom: 12, color: '#52c41a', fontWeight: 600 }}>
                      {localStorage.getItem('tempNewAdminRegistration') 
                        ? 'Hesabınız oluşturuldu ve sisteme otomatik giriş yaptınız!'
                        : 'Aboneliğiniz başarıyla yenilendi!'
                      }
                    </Text> */}
                    <Text style={{ fontSize: 14, display: 'block', marginBottom: 8, color: '#1890ff' }}>
                      Artık Dinox'un tüm premium özelliklerini kullanabilirsiniz.
                    </Text>
                  </div>
                }
                extra={[
                  <Button 
                    type="primary" 
                    key="dashboard"
                    icon={<HomeOutlined />}
                    onClick={handleGoToDashboard}
                    size="large"
                  >
                    {localStorage.getItem('tempNewAdminRegistration') 
                      ? 'Sisteme Giriş Yap' 
                      : 'Ana Sayfaya Git'
                    }
                  </Button>
                  // <Button 
                  //   key="settings"
                  //   icon={<CreditCardOutlined />}
                  //   onClick={handleGoToSettings}
                  // >
                  //   Abonelik Ayarları
                  // </Button>
                ]}
              />
            ) : (
              <Result
                icon={<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 72 }} />}
                title={
                  <Title level={3} style={{ color: '#ff4d4f', marginBottom: 16 }}>
                    {result?.subscription.status && result.subscription.status !== 'active' 
                      ? 'Abonelik Aktif Değil' 
                      : 'Ödeme Başarısız'
                    }
                  </Title>
                }
                subTitle={
                  <div>
                    <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                      {result?.message || 'Ödeme işlemi tamamlanamadı.'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                      {result?.subscription.status && result.subscription.status !== 'active'
                        ? `Abonelik durumu: ${result.subscription.status}`
                        : 'Lütfen kart bilgilerinizi kontrol edip tekrar deneyin.'
                      }
                    </Text>
                  </div>
                }
                extra={[
                  <Button 
                    type="primary" 
                    key="retry"
                    icon={<CreditCardOutlined />}
                    onClick={handleRetryPayment}
                    size="large"
                  >
                    Tekrar Dene
                  </Button>,
                  <Button 
                    key="dashboard"
                    icon={<HomeOutlined />}
                    onClick={handleGoToDashboard}
                  >
                    Ana Sayfaya Git
                  </Button>
                ]}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback; 